import { useState, useEffect } from "react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { Button } from "./Button";

const INITIAL_DELAY = 3000; // Wait 3 seconds before showing prompt

/**
 * PWA install prompt banner that appears at the bottom of the screen.
 * Shows different UI for Android/Chrome (native prompt) vs iOS (manual instructions).
 */
export function PWAInstallPrompt() {
  const { canInstall, showIOSInstallHint, promptInstall, dismissPrompt } =
    usePWAInstall();
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Delay showing the prompt to avoid interrupting initial experience
  useEffect(() => {
    if (!canInstall && !showIOSInstallHint) return;

    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, INITIAL_DELAY);

    return () => clearTimeout(timer);
  }, [canInstall, showIOSInstallHint]);

  const handleInstall = async () => {
    await promptInstall();
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    dismissPrompt();
  };

  const handleIOSInstall = () => {
    setShowIOSInstructions(true);
  };

  if (!showPrompt) return null;

  // iOS Safari install instructions modal
  if (showIOSInstallHint) {
    return (
      <>
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 safe-area-pb">
          <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl border-2 border-sunshine-300 overflow-hidden animate-slide-up">
            <div className="p-4 flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sunshine-400 to-sunshine-500 flex items-center justify-center flex-shrink-0 shadow-md">
                <span className="text-3xl">🐰</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-neutral-900 text-lg">
                  Add JumpBean to Home
                </h3>
                <p className="text-neutral-600 text-sm">
                  Play anytime, even offline!
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="p-2 -mr-2 hover:bg-neutral-100 rounded-full transition-colors"
                aria-label="Close"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="px-4 pb-4 flex gap-3">
              <button
                onClick={handleDismiss}
                className="flex-1 py-3 text-neutral-600 font-semibold rounded-xl hover:bg-neutral-100 transition-colors"
              >
                Not Now
              </button>
              <Button
                variant="primary"
                size="md"
                onClick={handleIOSInstall}
                className="flex-1"
              >
                Show Me How
              </Button>
            </div>
          </div>
        </div>

        {/* iOS instructions modal */}
        {showIOSInstructions && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
            <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl animate-scale-up">
              <h2 className="font-display font-bold text-2xl text-center text-neutral-900 mb-6">
                Add to Home Screen
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 font-display font-bold text-sky-600">
                    1
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">
                      Tap the Share button
                    </p>
                    <p className="text-sm text-neutral-600 mt-1">
                      Look for <ShareIcon className="inline w-5 h-5 align-text-bottom" /> at the bottom of Safari
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 font-display font-bold text-sky-600">
                    2
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">
                      Scroll and tap "Add to Home Screen"
                    </p>
                    <p className="text-sm text-neutral-600 mt-1">
                      Look for <AddToHomeIcon className="inline w-5 h-5 align-text-bottom" /> in the menu
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 font-display font-bold text-sky-600">
                    3
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">
                      Tap "Add" to confirm
                    </p>
                    <p className="text-sm text-neutral-600 mt-1">
                      JumpBean will appear on your home screen!
                    </p>
                  </div>
                </div>
              </div>

              <Button
                variant="primary"
                fullWidth
                onClick={() => {
                  setShowIOSInstructions(false);
                  handleDismiss();
                }}
              >
                Got It!
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Android/Chrome native install prompt
  if (canInstall) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 safe-area-pb">
        <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl border-2 border-sunshine-300 overflow-hidden animate-slide-up">
          <div className="p-4 flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sunshine-400 to-sunshine-500 flex items-center justify-center flex-shrink-0 shadow-md">
              <span className="text-3xl">🐰</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-neutral-900 text-lg">
                Install JumpBean
              </h3>
              <p className="text-neutral-600 text-sm">
                Quick access from your home screen!
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="p-2 -mr-2 hover:bg-neutral-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="px-4 pb-4 flex gap-3">
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 text-neutral-600 font-semibold rounded-xl hover:bg-neutral-100 transition-colors"
            >
              Not Now
            </button>
            <Button
              variant="primary"
              size="md"
              onClick={handleInstall}
              className="flex-1"
            >
              Install
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function CloseIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-neutral-400"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ShareIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`text-sky-600 ${className}`}
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16,6 12,2 8,6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

function AddToHomeIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`text-sky-600 ${className}`}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

export default PWAInstallPrompt;
